import React, { useState } from 'react';
import { useRouter } from 'next/router';
import { useDateNow, useDispatch, useReduxState } from 'hooks';
import { getCalendarEventDateString } from 'shared-helpers/calendar';
import {
  getDefaultEmailTemplate,
  renderPlaceholdersInTemplates
} from 'shared-helpers/email';
import api from 'api';
import { useApiData } from 'lib/api';
import { ConfirmButton, ConfirmDeleteButton } from 'components/common';
import { Box } from 'components/common/box';
import { CalendarEventDeleteSendInfo } from '.';

import type { GetEmailTemplatesByTypeRes } from 'shared-types/responses';

const CalendarEventDelete = () => {
  const { dateNow } = useDateNow();

  const { dispatch } = useDispatch();

  const {
    language,
    messages,
    pageState: { calendarEvent },
    serverConfig: { requireReasonWhenDeletingData, timeZone },
    user
  } = useReduxState();

  const router = useRouter();

  const [selectedRecipientsType, setSelectedRecipientsType] = useState('none');

  const { data: emailTemplatesData } = useApiData<GetEmailTemplatesByTypeRes>({
    url: `/admin/email-templates-data?type=calendar-event-canceled&calendarEventId=${calendarEvent.id}`
  });

  let emailTemplates = emailTemplatesData?.emailTemplates || [];

  emailTemplates = renderPlaceholdersInTemplates({
    emailTemplates,
    overridePlaceholderValues: {
      contactEmail: user?.email,
      eventTime: getCalendarEventDateString({
        startDate: new Date(calendarEvent.startDate),
        endDate: calendarEvent.endDate
          ? new Date(calendarEvent.endDate)
          : undefined,
        timeZone
      }),
      eventTitle: calendarEvent.title
    }
  });

  const emailTemplatesDefault = getDefaultEmailTemplate({
    language,
    emailTemplates
  });

  const handleSubmit = async (values = null) => {
    try {
      await api.post(`/admin/calendar-events/${calendarEvent.id}`, {
        ...(values ? values : {})
      });

      router.push('/calendar');
      dispatch.addNotice({
        text: messages.eventCalendarDeleteEventSuccess,
        type: 'success'
      });
      return true;
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
      return false;
    }
  };

  const showSendInfoModal =
    calendarEvent.numberOfParticipantsAdded > 0 &&
    new Date(calendarEvent.endDate).getTime() > dateNow;

  return (
    <Box title={messages.eventCalendarDeleteEvent}>
      {showSendInfoModal ? (
        <CalendarEventDeleteSendInfo
          emailTemplates={emailTemplates}
          emailTemplatesDefault={emailTemplatesDefault}
          selectedRecipientsType={selectedRecipientsType}
          setSelectedRecipientsType={setSelectedRecipientsType}
          onSubmit={handleSubmit}
        />
      ) : requireReasonWhenDeletingData ? (
        <ConfirmDeleteButton
          buttonText={messages.commonDelete}
          confirmText={messages.eventCalendarDeleteEventMessage}
          confirmTitle={messages.eventCalendarDeleteEvent}
          id="deletionReasonCalendarEvent"
          onConfirm={handleSubmit}
        />
      ) : (
        <ConfirmButton
          actionText={messages.eventCalendarDeleteEvent}
          buttonIcon="trash-alt"
          buttonText={messages.commonDelete}
          category="danger"
          confirmText={messages.eventCalendarDeleteEventMessage}
          confirmTitle={messages.eventCalendarDeleteEvent}
          onConfirm={handleSubmit}
        />
      )}
    </Box>
  );
};

export default CalendarEventDelete;
